import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  BASE_URL,
  VENDOR_SEASON_LIST_API_PATH,
  VENDOR_SEASON_LIST_CONTENTS_SUB_PATH,
} from 'constants/api';
import { accountStore } from 'store/accountStore';
import {
  VendorSeasonListContentRow,
  VendorSeasonListContentsResponse,
} from 'type/vendorContentType';

const stripLeadingSlash = (s: string): string => (s.startsWith('/') ? s.slice(1) : s);

const ENDPOINT = `${VENDOR_SEASON_LIST_API_PATH}${stripLeadingSlash(
  VENDOR_SEASON_LIST_CONTENTS_SUB_PATH
)}`;

export interface UseVendorSeasonListContentsParams {
  seasonId?: number | null;
  page?: number;
  pageSize?: number;
  // BE 필터 파라미터
  ingestSource?: number | null;
  uploadedAfter?: string | null; // YYYY-MM-DD
  uploadedBefore?: string | null; // YYYY-MM-DD
  hasOrigin?: boolean | null;
  search?: string | null;
  // DRF ordering: 'episode_order' | '-episode_order' | 'file_uploaded_at' | '-file_uploaded_at' | 'id' | '-id'
  ordering?: string | null;
}

export const VENDOR_SEASON_LIST_CONTENTS_KEY = (
  userId: number | undefined,
  params: UseVendorSeasonListContentsParams
) =>
  [
    'vendor-season-list-contents',
    userId ?? null,
    params.seasonId ?? null,
    params.page ?? 1,
    params.pageSize ?? 20,
    params.ingestSource ?? null,
    params.uploadedAfter ?? null,
    params.uploadedBefore ?? null,
    params.hasOrigin ?? null,
    params.search ?? null,
    params.ordering ?? null,
  ] as const;

/**
 * /api/v1/vendor/season-list/contents/ 호출.
 * 현재 로그인 user의 매핑만 대상. user_id를 명시적으로 쿼리에 전달.
 * BE 필터: ingest_source, uploaded_after/before, has_origin, search, ordering.
 */
export const useVendorSeasonListContents = (params: UseVendorSeasonListContentsParams = {}) => {
  const {
    seasonId,
    page = 1,
    pageSize = 20,
    ingestSource,
    uploadedAfter,
    uploadedBefore,
    hasOrigin,
    search,
    ordering,
  } = params;
  const account = accountStore.getAccount();
  const userId = account ? Number(account.id) : undefined;

  return useQuery<VendorSeasonListContentsResponse>({
    queryKey: VENDOR_SEASON_LIST_CONTENTS_KEY(userId, params),
    queryFn: async () => {
      const query = new URLSearchParams();
      if (userId) query.set('user_id', String(userId));
      query.set('page', String(page));
      query.set('page_size', String(pageSize));
      if (seasonId) query.set('season_id', String(seasonId));
      if (typeof ingestSource === 'number') query.set('ingest_source', String(ingestSource));
      if (uploadedAfter) query.set('uploaded_after', uploadedAfter);
      if (uploadedBefore) query.set('uploaded_before', uploadedBefore);
      if (typeof hasOrigin === 'boolean') query.set('has_origin', hasOrigin ? 'true' : 'false');
      if (search) query.set('search', search);
      if (ordering) query.set('ordering', ordering);
      const response = await axios.get(`${BASE_URL}${ENDPOINT}?${query.toString()}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
};

export const findContentInList = (
  rows: VendorSeasonListContentRow[] | undefined,
  contentId: number
): VendorSeasonListContentRow | undefined => rows?.find(row => row.content_id === contentId);
